'use client';

import type { ComponentProps, FC, PropsWithChildren } from 'react';
import { useId } from 'react';
import { Tooltip } from 'react-tooltip';
import type { VectorName } from '@/ui/components/VectorGraphic/VectorGraphic';
import { Link } from '@/core/i18n/routing';
import { trackInteraction } from '@/core/services/ga4/ga4-service';
import VectorGraphic from '@/ui/components/VectorGraphic/VectorGraphic';
import cn from '@/ui/utils/cn/cn';
import type { TypographyWeight } from '../Typography/Typography';
import Typography from '../Typography/Typography';

import '@/ui/styles/tooltip.css';
import $ from './Button.module.scss';

export type ButtonVariant =
  | 'primary'
  | 'primaryOutline'
  | 'secondary'
  | 'secondaryOutline'
  | 'tertiary'
  | 'tertiaryOutline'
  | 'quaternary'
  | 'quinary'
  | 'dark'
  | 'danger';

type ButtonProps = PropsWithChildren<{
  type?: 'submit' | 'button';
  disabled?: boolean;
  onClick?: () => void;
  className?: string;
  /** Wraps icon + children in a span (e.g. shared :hover for header cart icon + count). */
  contentWrapperClassName?: string;
  testid?: string;
  variant?: ButtonVariant;
  fullWidth?: boolean;
  icon?: VectorName;
  iconClassName?: string;
  iconPosition?: 'left' | 'right';
  ariaLabel?: string;
  href?: ComponentProps<typeof Link>['href'] | string;
  isExternal?: boolean;
  isReversed?: boolean;
  isLoading?: boolean;
  trackingTarget?: string | ComponentProps<typeof Link>['href'];
  trackingName?: string;
  trackingText?: string;
  tooltipMessage?: string;
  weight?: TypographyWeight;
  uppercase?: boolean;
}>;

const Button: FC<ButtonProps> = ({
  type = 'button',
  variant = 'primary',
  disabled,
  onClick,
  children,
  className = '',
  contentWrapperClassName,
  testid,
  fullWidth,
  icon,
  iconClassName = $.iconDefault,
  iconPosition = 'left',
  ariaLabel,
  href,
  isExternal,
  isReversed,
  isLoading = false,
  trackingTarget,
  trackingName,
  trackingText,
  tooltipMessage,
  weight,
  uppercase = true,
}) => {
  const tooltipId = useId();

  const handleTracking = (interactionType: 'hyperlink' | 'click') => {
    if (!href && !trackingTarget) return;

    trackInteraction({
      interaction_type: interactionType,
      click_target: href || trackingTarget || '',
      click_text: trackingText,
      content_name: trackingName,
    });
  };

  const handleInteraction = (interactionType: 'hyperlink' | 'click') => {
    handleTracking(interactionType);
    onClick?.();
  };

  const renderContent = () => {
    if (isLoading) {
      return (
        <div className={$.loadingWrapper}>
          <span className={$.dot} />
          <span className={$.dot} />
          <span className={$.dot} />
        </div>
      );
    }
    const inner = (
      <>
        {icon && iconPosition === 'left' && (
          <VectorGraphic name={icon} className={iconClassName} />
        )}
        {children && (
          <Typography variant="button" weight={weight}>
            {children}
          </Typography>
        )}
        {icon && iconPosition === 'right' && (
          <VectorGraphic name={icon} className={iconClassName} />
        )}
      </>
    );

    if (contentWrapperClassName) {
      return <span className={contentWrapperClassName}>{inner}</span>;
    }

    return inner;
  };

  const classNames = cn(
    $[variant],
    $.button,
    fullWidth && $.fullWidth,
    isReversed && $.isReversed,
    isLoading && $.loading,
    uppercase && $.uppercase,
    className,
  );

  const isActuallyDisabled = disabled || isLoading;
  const shouldShowDisabledTooltip = Boolean(
    isActuallyDisabled && tooltipMessage,
  );

  if (href) {
    const commonProps = {
      'data-testid': testid,
      className: classNames,
      'aria-label': ariaLabel,
      'aria-disabled': isLoading,
      onClick: () => handleInteraction('hyperlink'),
      children: renderContent(),
    };

    if (isExternal) {
      return (
        <a
          {...commonProps}
          href={(href as string) || '/'}
          rel="noopener noreferrer"
          target="_blank"
        >
          {renderContent()}
        </a>
      );
    }

    return <Link {...commonProps} href={href || '/'} />;
  }

  return (
    <>
      {shouldShowDisabledTooltip ? (
        <>
          <span
            data-tooltip-id={tooltipId}
            data-tooltip-content={tooltipMessage}
            className={$.tooltipWrapper}
          >
            <button
              data-testid={testid}
              type={type}
              disabled={isActuallyDisabled}
              onClick={() => handleInteraction('click')}
              className={classNames}
              aria-label={ariaLabel}
            >
              {renderContent()}
            </button>
          </span>
          <Tooltip
            id={tooltipId}
            className={$.tooltip}
            positionStrategy="fixed"
          />
        </>
      ) : (
        <button
          data-testid={testid}
          type={type}
          disabled={isActuallyDisabled}
          onClick={() => handleInteraction('click')}
          className={classNames}
          aria-label={ariaLabel}
        >
          {renderContent()}
        </button>
      )}
    </>
  );
};
export default Button;
